export const ACTIVITY_FIELD = {
  TITLE: "title",
  EXPIRE_DATE: "expireDate",
  START_DATE: "startDate",
  END_DATE: "endDate",
  DATE_RANGE: "dateRange",
  START_EXPIRE_ORDER: "startExpireOrder",
  FILE_EXTENSION: "fileExtension",
  COST: "cost",
};

const COST_DECIMAL_PRECISION = 18;
const COST_DECIMAL_SCALE = 2;
const COST_MAX_INTEGER_DIGITS = COST_DECIMAL_PRECISION - COST_DECIMAL_SCALE;

export const costFromActivity = (value) => {
  if (value == null || value === "") {
    return "";
  }
  const normalizedValue = String(value).replace(",", ".");
  if (normalizedValue === "") {
    return "";
  }
  const numberValue = Number(normalizedValue);
  if (Number.isNaN(numberValue)) {
    return "";
  }
  return normalizedValue;
};

export const sanitizeCostInput = (raw) => {
  const sanitized = String(raw)
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "");
  const dotIndex = sanitized.indexOf(".");
  if (dotIndex === -1) {
    return sanitized;
  }

  const intPart = sanitized.slice(0, dotIndex).replace(/\./g, "");
  const decPart = sanitized
    .slice(dotIndex + 1)
    .replace(/\./g, "")
    .slice(0, 2);

  return decPart.length > 0 ? `${intPart}.${decPart}` : `${intPart}.`;
};

export const parseCostForSubmit = (costStr) => {
  const costValue = String(costStr ?? "").trim();
  if (costValue === "" || costValue === ".") {
    return null;
  }

  const normalized = costValue.endsWith(".")
    ? costValue.slice(0, -1)
    : costValue;
  if (normalized === "") {
    return null;
  }

  const numericValue = parseFloat(normalized);
  if (Number.isNaN(numericValue)) {
    return null;
  }

  return Math.round(numericValue * 100) / 100;
};

export const isCostValueValid = (costStr) => {
  const costValue = String(costStr ?? "").trim();
  if (costValue === "") {
    return true;
  }
  if (costValue === ".") {
    return false;
  }

  const normalized = costValue.endsWith(".")
    ? costValue.slice(0, -1)
    : costValue;
  if (normalized === "") {
    return false;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    return false;
  }

  const [intRaw, fracRaw = ""] = normalized.split(".");
  const intSignificant = intRaw.replace(/^0+/, "") || "0";
  if (intSignificant.length > COST_MAX_INTEGER_DIGITS) {
    return false;
  }
  if (fracRaw.length > COST_DECIMAL_SCALE) {
    return false;
  }

  return true;
};

export const resolveActivityCertificateId = (activity) => {
  if (!activity) {
    return null;
  }

  const rawValue = activity.certificateId ?? null;
  if (rawValue == null || rawValue === "") {
    return null;
  }
  if (typeof rawValue === "number") {
    return rawValue;
  }

  const numericValue = Number(rawValue);
  return Number.isNaN(numericValue) ? null : numericValue;
};

export const certificateIdToSelectValue = (certificateId) => {
  if (certificateId == null || certificateId === "") {
    return "";
  }
  return String(certificateId);
};

export const getDeadlineDate = (activityData, activity) => {
  return (
    activityData.expireDate || activity.dueDate || activity.deadlineDate || ""
  );
};

export const isAllowedExtension = (fileName, allowedExtensions) => {
  const extension = fileName.split(".").pop().toLowerCase();
  return allowedExtensions.includes(extension);
};

const buildItemMetadataPayload = (
  itemMetadataFields,
  itemMetadataValuesById,
) => {
  if (itemMetadataFields.length === 0) {
    return {};
  }

  return {
    itemMetadata: JSON.stringify(
      itemMetadataFields.map((field) => ({
        id: field.id,
        value: itemMetadataValuesById[field.id] ?? "",
      })),
    ),
  };
};

export const buildSubmitData = ({
  fromEducationHistory,
  personalId,
  activity,
  activityData,
  itemMetadataFields,
  itemMetadataValuesById,
  deadlineDate,
  priorityValue,
}) => {
  const itemMetadataProp = buildItemMetadataPayload(
    itemMetadataFields,
    itemMetadataValuesById,
  );

  if (fromEducationHistory) {
    return {
      personalId,
      attachmentId: activity.attachmentId,
      title: activityData.title,
      comments: activityData.comment || "-",
      isCompleted: true,
      createdBy: personalId,
      description: activityData.description,
      startDate: activityData.startDate,
      endDate: activityData.endDate,
      isOtherTraining: false,
      removeAttachment: activityData.removeAttachment,
      hours: activityData.hours,
      minutes: activityData.minutes,
      cost: parseCostForSubmit(activityData.cost),
      ...itemMetadataProp,
    };
  }

  return {
    personalId,
    attachmentId: activity.attachmentId,
    compTalkId: null,
    courseObjectId: null,
    title: activityData.title,
    comments: activityData.comment || "-",
    isCompleted: activityData.isCompleted,
    createdBy: personalId,
    period: "-",
    priority: priorityValue || "-",
    description: activityData.description,
    showComment: false,
    startDate: activityData.startDate,
    endDate: activityData.endDate,
    isOtherTraining: false,
    courseId: null,
    deadlineDate,
    personalGoalId: activityData.personalGoalId,
    responsible: activityData.responsible,
    link: activityData.link,
    linkLabel: activityData.linkLabel,
    removeAttachment: activityData.removeAttachment,
    hours: activityData.hours,
    minutes: activityData.minutes,
    cost: parseCostForSubmit(activityData.cost),
    isExternalCourse: true,
    certificateId: activityData.certificateId,
    ...itemMetadataProp,
  };
};

export const shouldShowCompletedCheckbox = ({
  fromEducationHistory,
  isManager,
  allowUsersToCompleteCourses,
  requireApprovalForCompletion,
  isApproved,
}) => {
  if (fromEducationHistory) {
    return false;
  }

  if (isManager) {
    return true;
  }

  if (allowUsersToCompleteCourses === "1" && !requireApprovalForCompletion) {
    return true;
  }

  if (requireApprovalForCompletion && isApproved) {
    return true;
  }

  return false;
};
